<template>
  <va-modal
    id="userActivitiesModal"
    ref="userActivitiesModal"
    size="small"
    :value="isOpen"
    :noOutsideDismiss="true"
    :fixedLayout="true"
    @ok="save"
    :okText="$t('modals.buttons.save')"
    :okDisabled="isSubmitting"
    @cancel="closeModal"
    :cancelText="$t('modals.buttons.cancel')"
  >
    <div slot="header" class="modal-title">
      {{ modalTitle }}
    </div>

    <form class="pb-4 evollu-form">
      <FadeTransition mode="out-in" :duration="250">
        <div key="appointments-warning" class="row negative-mt-4" v-if="appointments.length">
          <div class="col">
            <small>
              {{
                $t('entities.activities.update_with_appointments', {
                  start: appointmentMinStart,
                  end: appointmentMaxEnd
                })
              }}
            </small>
          </div>
        </div>
      </FadeTransition>

      <FadeTransition mode="out-in" :duration="250">
        <div key="provider-warning" class="row" v-if="formErrors.has('provider_id')">
          <div class="col">
            <small class="text text-danger">
              {{ $t(formErrors.get('provider_id')) }}
            </small>
          </div>
        </div>
      </FadeTransition>

      <!-- Entidade -->
      <div class="row">
        <div class="col">
          <div>
            <label class="form-label">{{ $t('entities.activities.revision.entity') }}</label>
            <multiselect
              v-model="form.entity"
              :options="availableEntities"
              track-by="id"
              label="name"
              :custom-label="formatedEntityLabel"
              select-label=""
              selected-label=""
              deselect-label=""
              :disabled="isUpdating || this.entity !== null"
              :placeholder="$t('facility.inputs.entity')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              :show-no-options="false"
            >
              <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
            </multiselect>
            <small v-show="formErrors.has('entity_id')" class="help text-danger">
              {{ formErrors.get('entity_id') | translate }}
            </small>
          </div>
        </div>
      </div>

      <!-- Utilizador -->
      <div class="row" v-if="!pending">
        <div class="col">
          <div>
            <label class="form-label">{{ $t('entities.activities.revision.user') }}</label>
            <multiselect
              v-model="form.user"
              :options="availableUsers"
              track-by="id"
              label="name"
              select-label=""
              selected-label=""
              deselect-label=""
              :max-height="150"
              :disabled="isUpdating || this.user !== null"
              :placeholder="$t('entities.roles.header_name')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :show-no-options="false"
            >
              <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
              <template slot="singleLabel" slot-scope="props">
                <span>{{ `${props.option.name}` }}</span>
              </template>
              <template slot="option" slot-scope="props">
                <span>{{ `${props.option.name}` }}</span>
              </template>
            </multiselect>
            <small v-show="formErrors.has('user_id')" class="help text-danger">
              {{ formErrors.get('user_id') | translate }}
            </small>
          </div>
        </div>
      </div>

      <!-- Atividade -->
      <div class="row">
        <div class="col">
          <div>
            <label class="form-label">{{ $t('entities.activities.revision.activity') }}</label>
            <multiselect
              v-model="form.activity"
              :options="availableActivities"
              track-by="id"
              label="name"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('users.activities.activity')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              :show-no-options="false"
              :disabled="isUpdating"
            >
              <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
              <template slot="singleLabel" slot-scope="props">
                <span>{{ $t(`evocall.activities.${props.option.id}`, props.option.name) }}</span>
              </template>
              <template slot="option" slot-scope="props">
                <span>{{ $t(`evocall.activities.${props.option.id}`, props.option.name) }}</span>
              </template>
            </multiselect>
            <small v-show="formErrors.has('activity_id')" class="help text-danger">
              {{ formErrors.get('activity_id') | translate }}
            </small>
          </div>
        </div>
      </div>

      <!-- Datas -->
      <div class="row mb-3">
        <div class="col-6">
          <date-picker
            id="activity-start-date"
            v-model="form.start_date"
            :label="$t('licenses.start_date')"
            :config="{enableTime: true, time_24hr: true, minuteIncrement: 15, dateFormat: 'Y-m-d H:i', defaultHour: '08'}"
            :error="formErrors.has('start_date')"
            :error-messages="[$t(formErrors.get('start_date'))]"
            :clearable="true"
            @input="formErrors.clear('start_date')"
          />
        </div>

        <div class="col-6">
          <date-picker
            id="activity-end-date"
            v-model="form.end_date"
            :label="$t('licenses.end_date')"
            :config="{enableTime: true, time_24hr: true, minuteIncrement: 15, dateFormat: 'Y-m-d H:i', defaultHour: '20'}"
            :error="formErrors.has('end_date')"
            :error-messages="[$t(formErrors.get('end_date'))]"
            :clearable="true"
            @input="formErrors.clear('end_date')"
          />
        </div>
        <small v-show="formErrors.has('overlap')" class="col-12 help text-danger mt-1">
          {{ $t(formErrors.get('overlap')) }}
        </small>
      </div>

      <div class="row" v-if="isUpdating && selectedActivity.status_id === ActivityStatus.PENDING">
        <div class="col-12">
          <label class="form-label">{{ $t('entities.activities.status') }}</label>
          <div class="d-flex">
            <va-radio-button
              class="mr-3"
              v-for="(option, index) in availableStatuses"
              v-model="form.status_id"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
            />
          </div>
        </div>
      </div>

    </form>
  </va-modal>
</template>
<script>
import DatePicker from '@/components/common/DatePicker'
import Multiselect from 'vue-multiselect'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import Errors from '@/utils/CustomNodeErrors.js'
import { uniqBy, flatten } from 'lodash'
import { EntityActivityUserService, WorkActivityService, UserService } from '@/api/AuthService'
import { AppointmentService } from '@/api/AuthClinicalService'
import { ACTIVITY_STATUSES_OF_APPROVAL } from '@/components/entity-activity-user/config'
import MultiselectLabelFormatterVue from '../../../../mixins/MultiselectLabelFormatter.vue'

const DEFAULT_START_TIME = 8
const ActivitiesInfoType = 140
const ActivitiesPendingInfoType = 141
const errorsPrefix = 'entities.activities.errors.'

const ActivityStatus = {
  ACTIVE: 1,
  PENDING: 2,
  INACTIVE: 3
}

export default {
  components: {
    DatePicker,
    Multiselect,
    FadeTransition,
  },
  mixins: [
    MultiselectLabelFormatterVue
  ],
  props: {
    user: {
      type: Object,
      required: false,
      default: null
    },
    entity: {
      type: Object,
      required: false,
      default: null
    },
    selectedActivity: {
      type: Object,
      required: false,
      default: null
    },
    pending: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      isOpen: false,
      isLoading: true,
      isSubmitting: false,
      form: {
        user: null,
        entity: null,
        activity: null,
        start_date: null,
        end_date: null,
        status_id: null
      },
      formErrors: new Errors(),
      hasErrors: false,
      availableActivities: [],
      availableEntities: [],
      availableUsers: [],
      availableStatuses: ACTIVITY_STATUSES_OF_APPROVAL.filter(status => status.assignable),
      ActivityStatus,
      appointments: []
    }
  },
  created: function () {
    if (this.isUpdating) {
      if (this.selectedActivity && this.selectedActivity.status_id === ActivityStatus.ACTIVE) {
        this.loadAppointments()
      }
      this.fillForm()
    } else {
      this.preFillForm()
    }
  },
  mounted: function () {
    this.isOpen = true
  },
  methods: {
    fillForm: function () {
      this.form = {
        ...JSON.parse(JSON.stringify(this.selectedActivity)),
        start_date: this.$options.filters.localTime(this.selectedActivity.start_date, 'YYYY-MM-DD HH:mm'),
        end_date: this.$options.filters.localTime(this.selectedActivity.end_date, 'YYYY-MM-DD HH:mm'),
        status_id: this.selectedActivity.status_id
      }
      this.availableUsers = [this.selectedActivity.user]
    },
    preFillForm: function () {
      if (this.pending) {
        this.form.status_id = ActivityStatus.PENDING
      } else {
        this.form.status_id = ActivityStatus.ACTIVE
      }
      if (this.entity && this.entity.id) {
        this.form.entity = this.entity
        this.getAvailableUsers()
      } else if (this.user) {
        this.form.user = this.user
        this.getAvailableEntities()
      }
      this.form.start_date = this.moment().add(1, 'day').set({
        hour: DEFAULT_START_TIME,
        minutes: 0,
        seconds: 0
      }).format('YYYY-MM-DD HH:mm')
    },
    getFormData: function () {
      return {
        status_id: this.form.status_id,
        entity_id: this.form.entity ? this.form.entity.id : null,
        activity_id: this.form.activity ? this.form.activity.id : null,
        user_id: this.form.user ? this.form.user.id : null,
        start_date: this.$options.filters.utcTime(this.form.start_date),
        end_date: this.$options.filters.utcTime(this.form.end_date)
      }
    },
    save: function () {
      let data = this.getFormData()

      let errors = this.checkValidations(data)
      if (errors.length) {
        this.formErrors.record(errors, errorsPrefix)
        return
      }

      this.isUpdating ? this.update(data) : this.create(data)
    },
    checkValidations: function (form) {
      let requiredFields = ['activity_id', 'entity_id', 'user_id']
      return Object.keys(form)
        .filter(property => {
          return requiredFields.includes(property) && form[property] === null
        })
        .map(property => {
          return {
            param: property,
            location: 'body',
            msg: `The field ${property} is invalid`,
            value: null
          }
        })
    },
    create: function (data) {
      this.isSubmitting = true
      EntityActivityUserService.store(data)
        .then(entityActivityUser => {
          this.$emit('created', { entityActivityUser })
          this.showToast(this.$t('entities.activities.created'), this.$store.state.successToastOptions)
          this.closeModal()
        }).catch(errors => {
          this.showToast(this.$t('entities.activities.create_error'), this.$store.state.errorToastOptions)
          if (errors.response.status === 422) {
            this.formErrors.record(errors.response.data.errors, errorsPrefix)
          }
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    update: function (data) {
      this.isSubmitting = true
      EntityActivityUserService.update(this.selectedActivity.id, data)
        .then(entityActivityUser => {
          this.$emit('updated', { entityActivityUser })
          this.showToast(this.$t('entities.activities.edited'), this.$store.state.successToastOptions)
          if (this.selectedActivity.status_id === ActivityStatus.PENDING && data.status_id === ActivityStatus.ACTIVE) {
            this.$emit('showTransferAppointmentsModal', { entityActivityUser })
          }
          this.closeModal()
        }).catch(errors => {
          this.showToast(this.$t('entities.activities.edit_error'), this.$store.state.errorToastOptions)
          if (errors.response.status === 422) {
            if (errors.response.data.errors.some(e => e.param === 'entity_activity_user_id')) {
              this.$emit('showBulkUpdateModal', { updatingFormData: this.getFormData() })
            } else {
              this.formErrors.record(errors.response.data.errors, errorsPrefix)
            }
          }
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    closeModal: function () {
      this.isOpen = false
      this.$emit('modalClosed')
    },
    loadEntityAvailableActivities: function () {
      const userId = this.user ? this.user.entity_id : this.form.user.entity_id
      return new Promise((resolve, reject) => {
        WorkActivityService.query(this.form.entity.id, { 'editingUserEntityId': userId})
          .then(entitiesActivitiesProviders => {
            this.availableActivities = uniqBy(flatten(entitiesActivitiesProviders.filter(entityActivityProvider => {
              return entityActivityProvider.entity.id === this.form.entity.id && (entityActivityProvider.provider.id === userId || entityActivityProvider.provider.id === this.form.entity.id)
            }).map(entityActivityProvider => {
              return entityActivityProvider.activity
            })))
            resolve()
          }).catch(errors => {
            reject(errors)
          }).finally(() => {
            this.isLoading = false
          })
      })
    },
    getAvailableEntities: function () {
      let permissions = this.$store.getters.globalAuthService.user_permissions.filter(permission => permission.access_type === 2 && permission.info_type === this.infoType)
      if (permissions.some(permission => permission.entity_id === null)) {
        this.availableEntities = this.$store.getters.globalAuthService.entities
      } else {
        this.availableEntities = this.$store.getters.globalAuthService.entities.filter(entity => permissions.some(permission => permission.entity_id === entity.id))
      }
    },
    getAvailableUsers: function () {
      let entities = []
      let permissions = this.$store.getters.globalAuthService.user_permissions.filter(permission => permission.access_type === 2 && permission.info_type === this.infoType)
      if (permissions.some(permission => permission.entity_id === null)) {
        entities = this.$store.getters.globalAuthService.entities.map(entity => entity.id)
      } else {
        entities = this.$store.getters.globalAuthService.entities.filter(entity => permissions.some(permission => permission.entity_id === entity.id)).map(entity => entity.id)
      }
      if (!entities.length) {
        this.availableUsers = []
      } else {
        //let params = { entities: entities.join(',') }
        let params = { entity: this.entity.id }
        UserService.query(params)
          .then(users => {
            this.availableUsers = users.map(user => ({
              id: user.id,
              name: user.name,
              entity_id: user.entity_id,
              entity: user.entity
            }))
          }).catch(errors => {
            console.log(errors)
          }).finally(() => {
            this.isLoading = false
          })
      }
    },
    loadAppointments: function () {
      let params = {
        orderBy: 'start',
        ascending: 'asc'
      }
      AppointmentService.getByEntityActivityUser(this.$store.state.auth_clinical_server, this.selectedActivity.id, params)
        .then(appointments => {
          this.appointments = appointments
        }).catch(errors => {
          console.log(errors)
        })
    },
  },
  computed: {
    infoType: function () {
      return this.pending ? ActivitiesPendingInfoType : ActivitiesInfoType
    },
    appointmentMinStart: function () {
      return this.appointments.length ? this.$options.filters.localTime(this.appointments[0].start, 'YYYY-MM-DD HH:mm') : null
    },
    appointmentMaxEnd: function () {
      return this.appointments.length ? this.$options.filters.localTime(this.appointments[this.appointments.length - 1].end, 'YYYY-MM-DD HH:mm') : null
    },
    modalTitle: function () {
      let title = this.isUpdating ? this.$t('entities.activities.edit') : this.$t('entities.activities.new')
      return this.user ? `${title} - ${this.user.name}` : title
    },
    isUpdating: function () {
      return this.selectedActivity !== null
    },
    originalStartDatePast: function () {
      return this.selectedActivity && this.moment(this.$options.filters.localTime(this.selectedActivity.start_date)).isBefore(this.moment())
    },
  },
  watch: {
    'form.start_date': {
      handler: function (startDate) {
        if (startDate) {
          if (this.selectedActivity === null || !this.originalStartDatePast) {
            this.formErrors.clear('start_date')
            this.hasErrors = this.formErrors.any()
          }
          if (this.form.end_date) {
            if (this.moment(startDate).isAfter(this.moment(this.form.end_date))) {
              this.form.end_date = this.moment(`${startDate.substring(0, 10)} ${this.form.end_date.substring(11, 16)}`).add(1, 'month').format('YYYY-MM-DD HH:mm:ss')
            }
          }
        }
      },
      immediate: true,
      deep: true
    },
    'form.entity': {
      handler: function (entity) {
        if (entity && this.form.user !== null) {
          this.loadEntityAvailableActivities()
            .then(() => {
              if (!this.isUpdating && this.form.activity && !this.availableActivities.some(activity => activity.id === this.form.activity.id)) {
                this.form.activity = null
              }
              if (this.isUpdating && this.availableActivities.some(activity => activity.id === this.selectedActivity.activity.id)) {
                this.form.activity = this.selectedActivity.activity
              }
            })
        }
      },
      immediate: true,
      deep: true
    },
    'form.user': {
      handler: function (user) {
        if (user && this.form.entity !== null) {
          this.loadEntityAvailableActivities()
            .then(() => {
              if (!this.isUpdating && this.form.activity && !this.availableActivities.some(activity => activity.id === this.form.activity.id)) {
                this.form.activity = null
              }
              if (this.isUpdating && this.availableActivities.some(activity => activity.id === this.selectedActivity.activity.id)) {
                this.form.activity = this.selectedActivity.activity
              }
            })
        }
      },
      immediate: true,
      deep: true
    }
  }
}
</script>

<style lang="scss">

#userActivitiesModal {

  .modal-title {
    margin-bottom: 0;
  }

  .va-modal--fixed-layout .va-modal__inner .va-modal__header {
    box-shadow: none;
  }

  .negative-mt-4 {
    margin-top: - 1.5rem;
  }
}
</style>
